import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, Heart, ShoppingBag, Calendar, Crown, ArrowRight, TrendingUp, Award, Users, Star, Download, Share2, Menu, X, BookOpen, ShieldCheck, UsersRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n/I18nProvider";
import { copyToClipboard } from "@/lib/clipboard";
import { toast } from "sonner";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import HeaderInstallButton from "@/components/HeaderInstallButton";
import HomeBannerCarousel from "@/components/HomeBannerCarousel";
import ShopByCategories from "@/components/ShopByCategories";
import HomeProductRail from "@/components/HomeProductRail";
import RecentlyViewedRail from "@/components/RecentlyViewedRail";
import WalletCard from "@/components/WalletCard";
import DownloadAppCTA from "@/components/DownloadAppCTA";
import SearchBar from "@/components/SearchBar";
import { useHomeSections } from "@/hooks/useHomeSections";
import { useHeroConfig } from "@/hooks/useHeroConfig";
import { useLandingSections } from "@/hooks/useLandingSections";
import PlayStoreButton from "@/components/PlayStoreButton";
import WhatsAppOrderButton from "@/components/WhatsAppOrderButton";

const FEATURES = [
  { icon: ShoppingBag, title: "Marketplace", desc: "Sell handmade products, crystals, services. Build your business.", iconColor: "text-rose-600", bg: "bg-rose-50 hover:bg-rose-100 border-rose-200", iconBg: "bg-rose-100", path: "/marketplace", authRequired: false },
  { icon: Users, title: "Community Feed", desc: "Share wins, ask for help, and lift other women up.", iconColor: "text-emerald-600", bg: "bg-emerald-50 hover:bg-emerald-100 border-emerald-200", iconBg: "bg-emerald-100", path: "/community", authRequired: false },
  { icon: Calendar, title: "Meetups & Events", desc: "Attend city meetups, online summits, leadership bootcamps.", iconColor: "text-amber-600", bg: "bg-amber-50 hover:bg-amber-100 border-amber-200", iconBg: "bg-amber-100", path: "/events", authRequired: false },
  { icon: Crown, title: "Leadership Path", desc: "Climb from Shakti Mom to City Leader to State Leader.", iconColor: "text-violet-600", bg: "bg-violet-50 hover:bg-violet-100 border-violet-200", iconBg: "bg-violet-100", path: "/leadership", authRequired: false },
  { icon: TrendingUp, title: "Earnings Wallet", desc: "Three income streams. Withdraw to bank any time after ₹100.", iconColor: "text-lime-700", bg: "bg-lime-50 hover:bg-lime-100 border-lime-200", iconBg: "bg-lime-100", path: "/earnings", authRequired: false },
  { icon: Award, title: "Recognition", desc: "Badges, top sellers & leaderboards that celebrate your journey.", iconColor: "text-pink-600", bg: "bg-pink-50 hover:bg-pink-100 border-pink-200", iconBg: "bg-pink-100", path: "/recognition", authRequired: false },
];

// Early-stage realistic stats (Feb 2026 beta launch). Labels are translation keys.
const DEFAULT_STATS = [
  { num: "250+", labelKey: "stats.sisters" },
  { num: "₹2L+", labelKey: "stats.earnings" },
  { num: "12+", labelKey: "stats.cities" },
];

const TESTIMONIALS = [
  { name: "Anita Verma", city: "Delhi", quote: "From homemaker to ₹1L/month entrepreneur. Shakti Wings made it possible.", img: "https://images.unsplash.com/photo-1580489944761-15a19d654956?w=200" },
  { name: "Meera Reddy", city: "Bangalore", quote: "My home bakery is now a brand. The community lifted me up.", img: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=200" },
  { name: "Sneha Patel", city: "Ahmedabad", quote: "I never thought my craft would sell beyond my city. Now I ship across India.", img: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=200" },
];

export default function Landing() {
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const { config: hero } = useHeroConfig();
  const { config: ls } = useLandingSections();
  const { trending, new_arrivals, best_sellers, loading: railsLoading } = useHomeSections(12);
  const [content, setContent] = useState(null);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const bannerRef = useRef(null);
  const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0, isLive: false });

  useEffect(() => {
    api.get("/content").then(r => setContent(r.data)).catch(() => {});
    // Fetch only what we render (8 featured) instead of all products.
    api.get("/products?limit=8").then(r => setFeaturedProducts((r.data || []).slice(0, 8))).catch(() => {});
    // Idle-time prefetch: warm caches for likely next-pages (Marketplace data)
    // and start downloading their JS chunks so navigation feels instant.
    const idle = window.requestIdleCallback || ((cb) => setTimeout(cb, 1500));
    const id = idle(() => {
      try {
        api.get("/categories").catch(() => {});
        api.get("/products/category-counts").catch(() => {});
        // Prefetch route chunks
        import("@/pages/Marketplace");
        import("@/pages/Categories");
      } catch {}
    });
    return () => {
      if (window.cancelIdleCallback && typeof id === "number") {
        try { window.cancelIdleCallback(id); } catch {}
      }
    };
  }, []);

  // Live countdown to 1 June 2026 00:00:00 IST (UTC+5:30)
  useEffect(() => {
    const target = new Date("2026-06-01T00:00:00+05:30").getTime();
    const tick = () => {
      const diff = target - Date.now();
      if (diff <= 0) {
        setCountdown({ days: 0, hours: 0, minutes: 0, seconds: 0, isLive: true });
        return;
      }
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
      setCountdown({ days, hours, minutes, seconds, isLive: false });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  const downloadBanner = async () => {
    if (!bannerRef.current) return;
    try {
      toast.loading("Generating banner image...", { id: "dl-banner" });
      const html2canvas = (await import("html2canvas")).default;
      const canvas = await html2canvas(bannerRef.current, {
        backgroundColor: null,
        scale: 2,
        useCORS: true,
        logging: false,
      });
      const link = document.createElement("a");
      link.download = "shakti-wings-launch-1-june-2026.png";
      link.href = canvas.toDataURL("image/png");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success("Banner downloaded!", { id: "dl-banner" });
    } catch (e) {
      console.error("Banner download failed", e);
      toast.error("Download failed, please try again", { id: "dl-banner" });
    }
  };

  const shareEvent = async () => {
    const shareUrl = typeof window !== "undefined" ? window.location.origin : "";
    const shareData = {
      title: "Shakti Wings — Official Online Launch",
      text: "🦋 Shakti Wings Community — Official Online Launch on 1 June 2026!\n\"आओ जुड़ जाएँ... कुछ कर दिखाएँ...!\"\nRegister now:",
      url: shareUrl,
    };
    try {
      if (typeof navigator !== "undefined" && navigator.share) {
        await navigator.share(shareData);
        return;
      }
    } catch (e) {
      if (e?.name === "AbortError") return;
      console.warn("navigator.share failed, falling back", e);
    }
    // Fallback: WhatsApp share, then clipboard
    const wa = `https://wa.me/?text=${encodeURIComponent(`${shareData.text} ${shareData.url}`)}`;
    const win = typeof window !== "undefined" ? window.open(wa, "_blank", "noopener,noreferrer") : null;
    if (!win) {
      await copyToClipboard(`${shareData.text} ${shareData.url}`, { successMsg: "Event link copied!" });
    }
  };

  // Public sections → always accessible; auth-required → /auth wall
  const featurePath = (f) => {
    if (!f.authRequired) return f.path;
    return user ? f.path : `/auth?mode=signup&next=${encodeURIComponent(f.path)}`;
  };

  const c = content || {};
  // Hero strings — use translations when not English; fall back to CMS content for English (admin-editable)
  const heroPart1 = lang === "en" ? (c.hero_title_part1 || t("hero.title.1")) : t("hero.title.1");
  const heroPart2 = lang === "en" ? (c.hero_title_part2 || t("hero.title.2")) : t("hero.title.2");
  const heroPart3 = lang === "en" ? (c.hero_title_part3 || t("hero.title.3")) : t("hero.title.3");
  const heroSubtitle = lang === "en" ? (c.hero_subtitle || t("hero.subtitle")) : t("hero.subtitle");
  const stats = c.stats || DEFAULT_STATS;
  const ctaHeading = lang === "en" ? (c.cta_heading || "Join the community.") : "";
  const ctaSubheading = lang === "en" ? (c.cta_subheading || "Your wings are waiting. It takes 30 seconds to start. Free to join, forever.") : t("hero.subtitle");
  const testimonials = c.testimonials && c.testimonials.length > 0 ? c.testimonials : TESTIMONIALS;

  // Resolve a stat's label — supports either a translation key (default stats) or a literal admin-edited string
  const statLabel = (s) => (s.labelKey ? t(s.labelKey) : (s.label || ""));

  return (
    <div className="min-h-screen bg-pearl">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16 gap-2">
          <Link to="/" className="flex items-center gap-2 min-w-0" data-testid="landing-brand">
            <img src="/icons/icon-192.png" alt="Shakti Wings Logo" className="w-9 h-9 rounded-lg object-contain flex-shrink-0" />
            <span className="text-lg sm:text-2xl font-serif font-semibold text-amethyst-800 truncate">{t("brand.name")}</span>
          </Link>
          <nav className="hidden md:flex items-center gap-6 lg:gap-8">
            <Link to="/marketplace" className="text-sm font-medium text-gray-700 hover:text-amethyst-700">{t("nav.marketplace")}</Link>
            <Link to="/community" className="text-sm font-medium text-gray-700 hover:text-amethyst-700">{t("nav.community")}</Link>
            <Link to="/events" className="text-sm font-medium text-gray-700 hover:text-amethyst-700">{t("nav.events")}</Link>
            <Link to="/guide" className="text-sm font-medium text-gray-700 hover:text-amethyst-700">{t("nav.guide")}</Link>
          </nav>
          <div className="flex items-center gap-1.5 sm:gap-3 flex-shrink-0">
            <HeaderInstallButton />
            <LanguageSwitcher />
            <Link to="/auth" data-testid="header-login-btn" className="hidden sm:inline-block">
              <Button variant="ghost" className="text-amethyst-700 hover:bg-amethyst-50">{t("nav.login")}</Button>
            </Link>
            <Link to="/auth?mode=signup" data-testid="header-signup-btn">
              <Button className="btn-magenta rounded-full px-3 sm:px-6 text-xs sm:text-sm">{t("nav.join")}</Button>
            </Link>
            <button
              type="button"
              onClick={() => setMobileMenuOpen(true)}
              className="md:hidden p-2 rounded-lg hover:bg-amethyst-50 text-amethyst-700"
              aria-label="Open menu"
              data-testid="landing-mobile-menu-btn"
            >
              <Menu className="w-6 h-6" />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile menu drawer */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-[60] md:hidden" data-testid="landing-mobile-drawer">
          <div className="absolute inset-0 bg-amethyst-900/40 backdrop-blur-sm" onClick={() => setMobileMenuOpen(false)} />
          <div className="absolute top-0 right-0 bottom-0 w-72 max-w-[85%] bg-white shadow-2xl flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-amethyst-100">
              <span className="text-lg font-serif font-semibold text-amethyst-800">Menu</span>
              <button type="button" onClick={() => setMobileMenuOpen(false)} className="p-2 rounded-lg hover:bg-amethyst-50 text-amethyst-700" aria-label="Close menu">
                <X className="w-5 h-5" />
              </button>
            </div>
            <nav className="flex-1 p-4 space-y-1">
              {[
                { to: "/marketplace", icon: ShoppingBag, labelKey: "nav.marketplace" },
                { to: "/community", icon: Users, labelKey: "nav.community" },
                { to: "/events", icon: Calendar, labelKey: "nav.events" },
                { to: "/leadership", icon: Crown, labelKey: "nav.leaders" },
                { to: "/recognition", icon: Award, labelKey: "nav.stars" },
                { to: "/guide", icon: BookOpen, labelKey: "nav.guide" },
              ].map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-3 py-3 rounded-xl text-amethyst-800 hover:bg-amethyst-50 font-medium"
                  data-testid={`mobile-nav-${item.labelKey}`}
                >
                  <item.icon className="w-5 h-5 text-amethyst-500" strokeWidth={1.6} />
                  <span>{t(item.labelKey)}</span>
                </Link>
              ))}
            </nav>
            <div className="p-4 border-t border-amethyst-100 space-y-2">
              <Link to="/auth" onClick={() => setMobileMenuOpen(false)}>
                <Button variant="outline" className="w-full rounded-full border-amethyst-200 text-amethyst-700">{t("nav.login")}</Button>
              </Link>
              <Link to="/auth?mode=signup" onClick={() => setMobileMenuOpen(false)}>
                <Button className="w-full btn-magenta rounded-full">{t("nav.join")}</Button>
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Hero Banner Carousel — admin-managed home banners (Amazon/Flipkart style) */}
      <section className="pt-20 sm:pt-24 px-4 sm:px-6 lg:px-8" data-testid="home-banner-section">
        <div className="max-w-7xl mx-auto">
          <HomeBannerCarousel />
          {/* Sticky-style search bar directly under banner — DeoDap inspiration */}
          <div className="mt-4 sm:mt-6">
            <SearchBar />
          </div>
        </div>
      </section>

      {/* Shakti Wallet card — logged-in users see balance, guests see Login CTA */}
      {ls?.wallet_card_enabled !== false && <WalletCard />}

      {/* Shop by Categories — DeoDap-style category grid */}
      <ShopByCategories limit={12} />

      {/* Recently Viewed — session-scoped, only shows after user opens any product */}
      <RecentlyViewedRail />

      {/* Product discovery rails */}
      <HomeProductRail
        title="Trending Now"
        icon={TrendingUp}
        iconClass="text-magenta-600"
        products={trending}
        loading={railsLoading}
        viewAllHref="/search?sort=popular"
        testId="rail-trending"
      />
      <HomeProductRail
        title="New Arrivals"
        icon={Sparkles}
        iconClass="text-gold-500"
        products={new_arrivals}
        loading={railsLoading}
        viewAllHref="/search?sort=newest"
        testId="rail-new"
      />
      <HomeProductRail
        title="Best Sellers"
        icon={Award}
        iconClass="text-amethyst-700"
        products={best_sellers}
        loading={railsLoading}
        viewAllHref="/search?sort=popular"
        testId="rail-best"
      />

      {/* Download App CTA — always visible until installed */}
      <DownloadAppCTA />

      {/* Hero Banner — Admin-controlled (config from /api/hero-config) */}
      {hero.enabled !== false && (
      <section className="pt-6 pb-6 px-4 sm:px-6 lg:px-8" data-testid="launch-banner">
        <div className="max-w-7xl mx-auto">
          <div ref={bannerRef} className="relative overflow-hidden rounded-2xl sm:rounded-3xl bg-gradient-to-br from-amethyst-800 via-magenta-700 to-amethyst-900 shadow-premium">
            {/* Decorative gold accents */}
            <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-gold-400/20 blur-3xl" aria-hidden="true" />
            <div className="absolute -bottom-16 -left-16 w-56 h-56 rounded-full bg-magenta-500/20 blur-3xl" aria-hidden="true" />
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-gold-400 to-transparent" aria-hidden="true" />

            <div className="relative px-5 py-7 sm:px-8 sm:py-9 lg:px-12 lg:py-10 text-center lg:text-left">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
                <div className="flex-1 space-y-2 sm:space-y-3">
                  <p className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 backdrop-blur text-gold-200 text-[11px] sm:text-xs font-semibold tracking-[0.2em] uppercase">
                    {hero.top_label_emoji && <span aria-hidden="true">{hero.top_label_emoji}</span>} {hero.top_label}
                  </p>
                  <h2 className="text-2xl sm:text-3xl lg:text-4xl font-serif font-medium text-white leading-tight">
                    {hero.heading_emoji && <span aria-hidden="true">{hero.heading_emoji}</span>} {hero.heading}
                  </h2>
                  <p className="text-sm sm:text-base text-gold-200 font-medium flex items-center justify-center lg:justify-start gap-2">
                    {hero.subheading_emoji && <span aria-hidden="true">{hero.subheading_emoji}</span>}
                    <span>{hero.subheading}</span>
                  </p>
                  {hero.tagline && (
                    <p className="text-lg sm:text-xl lg:text-2xl text-gold-200 font-script pt-1 sm:pt-2">
                      &ldquo;{hero.tagline}&rdquo;
                    </p>
                  )}
                </div>

                {(hero.pill_label || hero.pill_subtitle) && (
                  <div className="flex-shrink-0" data-testid="career-highlight">
                    <div className="text-center px-6 py-4 rounded-2xl bg-gold-400/95 text-amethyst-900 max-w-xs">
                      {hero.pill_label && <p className="text-[10px] uppercase tracking-[0.2em] font-semibold">{hero.pill_label}</p>}
                      {hero.pill_subtitle && <p className="font-serif text-lg sm:text-xl font-semibold leading-tight mt-1">{hero.pill_subtitle}</p>}
                    </div>
                  </div>
                )}
              </div>

              {(hero.features || []).length > 0 && (
                <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-5 gap-y-2 text-left max-w-3xl mx-auto lg:mx-0" data-testid="career-features">
                  {hero.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm sm:text-[15px] text-white/95">
                      <span aria-hidden="true" className="text-gold-300 mt-0.5">✓</span>
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              )}

              <div className="mt-6 flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center justify-center lg:justify-start gap-3">
                {hero.button1_label && (
                  <Link to={hero.button1_link || "/marketplace"} data-testid="career-explore-btn" className="w-full sm:w-auto">
                    <Button className="bg-gold-400 hover:bg-gold-500 text-amethyst-900 font-semibold rounded-full px-7 sm:px-9 py-5 sm:py-6 text-sm sm:text-base shadow-lg transition-transform hover:scale-105 w-full">
                      {hero.button1_label}
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </Link>
                )}
                {hero.button2_label && (
                  <Link to={hero.button2_link || "/guide"} data-testid="career-growth-btn" className="w-full sm:w-auto">
                    <Button
                      variant="outline"
                      className="bg-white/10 hover:bg-white/20 text-white border-white/30 hover:border-white/50 font-medium rounded-full px-6 sm:px-7 py-5 sm:py-6 text-sm sm:text-base backdrop-blur transition w-full"
                    >
                      {hero.button2_label}
                    </Button>
                  </Link>
                )}
                {hero.button3_label && (
                  <Link to={hero.button3_link || "/auth?mode=signup"} data-testid="career-join-btn" className="w-full sm:w-auto">
                    <Button
                      variant="outline"
                      className="bg-white/10 hover:bg-white/20 text-white border-white/30 hover:border-white/50 font-medium rounded-full px-6 sm:px-7 py-5 sm:py-6 text-sm sm:text-base backdrop-blur transition w-full"
                    >
                      {hero.button3_label}
                    </Button>
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
      )}

      {/* Hero */}
      {ls.hero_enabled !== false && (
      <section className="hero-bg relative overflow-hidden pt-6 pb-20 lg:pt-10 lg:pb-28">
        <div className="grain absolute inset-0" />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 animate-fade-in-up">
              <div className="label-pill mb-6 bg-magenta-50 text-magenta-700" data-testid="hero-label">
                <Sparkles className="w-3 h-3 mr-2" strokeWidth={2} />
                {ls.hero_label || t("hero.label")}
              </div>
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-serif font-medium text-amethyst-900 leading-[1.05] tracking-tight">
                {ls.hero_title_part1 || heroPart1}<br />
                <span className="gradient-text-magenta italic">{ls.hero_title_part2 || heroPart2}</span> &<br />
                {ls.hero_title_part3 || heroPart3}
              </h1>
              <p className="mt-8 text-lg lg:text-xl text-gray-700 leading-relaxed max-w-2xl font-light">
                {ls.hero_subtitle || heroSubtitle}
              </p>

              {/* Mission Statement — pink accent card */}
              <div className="mt-8 max-w-2xl p-5 rounded-2xl bg-[#F5B6C8] border border-[rgba(11,31,58,0.25)] border-l-4 border-l-[#0B1F3A] shadow-premium" data-testid="hero-mission">
                <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#0B1F3A] mb-2">{ls.hero_mission_label || t("hero.mission.label")}</p>
                <p className="text-base lg:text-lg text-[#0B1F3A] font-serif italic leading-relaxed">
                  {ls.hero_mission_text || t("hero.mission.text")}
                </p>
              </div>

              <div className="mt-8 flex flex-col sm:flex-row gap-4">
                <Link to="/auth?mode=signup" data-testid="hero-cta-primary">
                  <Button className="btn-magenta rounded-full px-8 py-6 text-base font-medium w-full sm:w-auto">
                    {ls.hero_cta_primary || t("hero.cta.primary")}
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
                <Link to="/auth" data-testid="hero-cta-secondary">
                  <Button variant="outline" className="rounded-full px-8 py-6 text-base font-medium border-amethyst-300 text-amethyst-700 hover:bg-amethyst-50 w-full sm:w-auto">
                    {ls.hero_cta_secondary || t("hero.cta.secondary")}
                  </Button>
                </Link>
                <PlayStoreButton testId="hero-playstore-btn" className="w-full sm:w-auto justify-center" />
              </div>

              {/* Inline community proof */}
              <div className="mt-10 flex items-center gap-6 text-sm text-amethyst-700">
                <div className="flex -space-x-2">
                  {testimonials.slice(0, 3).map((tm, i) => (
                    tm.img ? (
                      <img key={tm.name || i} src={tm.img} alt={tm.name} className="w-10 h-10 rounded-full border-2 border-white object-cover" />
                    ) : (
                      <div key={tm.name || i} className="w-10 h-10 rounded-full border-2 border-white bg-gradient-to-br from-magenta-400 to-amethyst-500 flex items-center justify-center text-white text-sm font-serif font-semibold">{(tm.name || "S")[0]}</div>
                    )
                  ))}
                </div>
                <div>
                  <div className="flex items-center gap-1 mb-1">
                    {[1,2,3,4,5].map(i => <Star key={i} className="w-4 h-4 fill-gold-400 text-gold-400" />)}
                  </div>
                  <span className="font-medium">{stats[0]?.num || "250+"} {statLabel(stats[0] || {}).toLowerCase()}</span>
                </div>
              </div>
            </div>

            <div className="lg:col-span-5 relative">
              <div className="relative animate-float">
                <img
                  src="/images/hero-shakti-wings.webp"
                  alt="Shakti Wings — Empowering Women, Building Futures"
                  fetchPriority="high"
                  decoding="async"
                  className="rounded-3xl shadow-premium-lg object-contain object-bottom w-full h-[500px] bg-gradient-to-b from-amethyst-50 via-pearl to-pearl"
                />
                <div className="absolute -bottom-6 -left-6 float-card rounded-2xl p-5 max-w-[240px]">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-full bg-gold-100 flex items-center justify-center">
                      <Award className="w-5 h-5 text-gold-500" strokeWidth={1.5} />
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-wider text-amethyst-600">{ls.hero_earnings_label || statLabel(stats[1] || {}) || t("hero.earnings.label")}</p>
                      <p className="font-sans tabular-nums text-lg font-semibold text-amethyst-900">{ls.hero_earnings_num || stats[1]?.num || "₹2L+"}</p>
                    </div>
                  </div>
                  <p className="text-xs text-gray-600">{ls.hero_earnings_sub || t("hero.earnings.sub")}</p>
                </div>
                <div className="absolute -top-6 -right-6 rounded-2xl p-5 max-w-[220px] text-[#0B1F3A] border border-[rgba(11,31,58,0.25)]" style={{background: "#F5B6C8", boxShadow: "0 20px 50px rgba(11, 31, 58, 0.22)"}}>
                  <Heart className="w-5 h-5 text-[#C24B6E] mb-2" fill="currentColor" />
                  <p className="font-serif text-base leading-snug">&ldquo;{ls.hero_floating_quote || testimonials[0]?.quote?.slice(0, 60) || "My family's pride."}{(!ls.hero_floating_quote && (testimonials[0]?.quote?.length || 0) > 60) ? '…' : ''}&rdquo;</p>
                  <p className="text-xs text-[#0B1F3A]/75 mt-2">— {ls.hero_floating_quote_author || `${testimonials[0]?.name?.split(" ")[0] || "Priya"}, ${testimonials[0]?.city || "Mumbai"}`}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Stats strip — inside hero, on a glassy card */}
          <div className="mt-20 lg:mt-24 float-card rounded-3xl p-6 lg:p-8" data-testid="hero-stats-strip">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 lg:gap-8">
              {stats.map((s, i) => (
                <div key={s.labelKey || s.label || i} className="text-center">
                  <div className="text-3xl lg:text-4xl font-sans tabular-nums font-semibold gradient-text-magenta">{s.num}</div>
                  <div className="text-amethyst-700 text-xs lg:text-sm mt-1.5 uppercase tracking-wider font-medium">{statLabel(s)}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
      )}

      {/* Features */}
      {ls.features_enabled !== false && (
      <section id="features" className="py-20 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl mb-12 lg:mb-16">
            <div className="label-pill mb-4 bg-magenta-50 text-magenta-700">{ls.features_pill || "Everything in one place"}</div>
            <h2 className="text-4xl lg:text-5xl font-serif font-medium text-amethyst-900 tracking-tight">
              {ls.features_heading_pre || "One platform."} <span className="italic gradient-text-magenta">{ls.features_heading_em || "Everything"}</span> {ls.features_heading_post || "a woman needs to thrive."}
            </h2>
            <p className="mt-4 text-lg text-gray-600 font-light">
              {ls.features_subtitle || "From your first product sale to leading thousands of women — every tool you need, in one elegant home."}
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
            {FEATURES.map((f, idx) => (
              <Link
                key={f.title}
                to={featurePath(f)}
                className={`fade-up delay-${Math.min(idx + 1, 6)} ${f.bg} border-2 rounded-3xl p-7 lg:p-8 cursor-pointer group block focus:outline-none focus:ring-2 focus:ring-magenta-300 focus:ring-offset-2 shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-1`}
                data-testid={`feature-${f.title.toLowerCase().replace(/ /g, '-')}`}
              >
                <div className={`w-14 h-14 rounded-2xl ${f.iconBg} flex items-center justify-center mb-5 group-hover:scale-110 transition-transform`}>
                  <f.icon className={`w-7 h-7 ${f.iconColor}`} strokeWidth={1.5} />
                </div>
                <h3 className="text-2xl font-serif font-semibold text-amethyst-900 mb-2">{f.title}</h3>
                <p className="text-gray-700 leading-relaxed">{f.desc}</p>
                <div className={`mt-5 flex items-center gap-2 text-sm font-semibold ${f.iconColor} transition-colors`}>
                  <span>Explore</span>
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* Community proof */}
      <section id="community" className="py-20 lg:py-28 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none" style={{background: "radial-gradient(ellipse 50% 40% at 80% 30%, rgba(225, 71, 147, 0.08), transparent 70%), radial-gradient(ellipse 40% 30% at 10% 80%, rgba(232, 122, 156, 0.08), transparent 70%)"}} />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center relative">
          <div className="order-2 lg:order-1">
            <div className="relative">
              <img src="/images/hero-shakti-wings.webp" alt="Sisterhood of Shakti Wings women entrepreneurs" loading="lazy" decoding="async" className="rounded-3xl shadow-premium-lg w-full object-cover bg-gradient-to-b from-amethyst-50 to-pearl" />
              {/* Floating quote card on image */}
              <div className="absolute -bottom-5 -right-5 sm:-right-8 max-w-[260px] float-card rounded-2xl p-4">
                <div className="flex items-center gap-1 mb-1.5">
                  {[1,2,3,4,5].map(i => <Star key={i} className="w-3.5 h-3.5 fill-gold-400 text-gold-400" />)}
                </div>
                <p className="text-sm font-serif italic text-amethyst-900 leading-snug">"I found my voice, my income, and my soul-sisters here."</p>
                <p className="text-xs text-magenta-700 font-semibold mt-1.5">— A Shakti Mom</p>
              </div>
            </div>
          </div>
          <div className="order-1 lg:order-2">
            <div className="label-pill mb-4 bg-magenta-50 text-magenta-700">Our Sisterhood</div>
            <h2 className="text-4xl lg:text-5xl font-serif font-medium text-amethyst-900 leading-tight tracking-tight">
              When one woman rises, <br/>
              <span className="italic gradient-text-magenta">we all rise.</span>
            </h2>
            <p className="mt-6 text-lg text-gray-700 leading-relaxed font-light">
              Get mentored by State Leaders. Make friends in every city. Share wins, celebrate milestones, and pull each other up — because real change happens together, never alone.
            </p>
            <div className="mt-8 space-y-4">
              {[
                { title: "Daily wins & stories", desc: "A feed full of real entrepreneurial journeys, no influencer noise." },
                { title: "City meetups & state summits", desc: "Meet your tribe over chai, share notes, leave inspired." },
                { title: "Mentorship by senior leaders", desc: "1-on-1 guidance from women who've built businesses from their kitchen." },
                { title: "Safe space, zero judgement", desc: "Verified members only. No trolls, no spam — just sisterhood." },
              ].map((p) => (
                <div key={p.title} className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-magenta-400 to-amethyst-500 flex items-center justify-center flex-shrink-0">
                    <Sparkles className="w-3.5 h-3.5 text-white" strokeWidth={2.5} />
                  </div>
                  <div>
                    <p className="font-semibold text-amethyst-900">{p.title}</p>
                    <p className="text-sm text-gray-600 mt-0.5">{p.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Featured Products */}
      {featuredProducts.length > 0 && (
        <section id="products" className="py-16 lg:py-20 bg-gradient-to-b from-pearl via-amethyst-50/30 to-pearl" data-testid="featured-products-section">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="flex items-end justify-between gap-4 mb-8 flex-wrap">
              <div>
                <div className="label-pill mb-3 inline-flex items-center gap-1 bg-magenta-50 text-magenta-700">
                  <ShoppingBag className="w-3 h-3" /> Featured Products
                </div>
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-medium text-amethyst-900 leading-tight">
                  Shop our sisters&apos; <span className="italic gradient-text-gold">craft</span>
                </h2>
                <p className="text-gray-600 mt-2 max-w-xl">Handpicked products from women entrepreneurs across India. Pay COD or order directly via WhatsApp.</p>
              </div>
              <Link to="/marketplace" data-testid="view-all-products-btn" className="hidden sm:inline-block">
                <Button variant="outline" className="rounded-full border-amethyst-300 text-amethyst-700 hover:bg-amethyst-50 px-6">
                  View All Products
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-5">
              {featuredProducts.map((p) => (
                <div key={p.id} className="card-premium overflow-hidden flex flex-col" data-testid={`featured-product-${p.id}`}>
                  <Link to="/marketplace" className="aspect-square bg-white overflow-hidden block">
                    {p.image_url ? (
                      <img src={p.image_url} alt={p.name} className="w-full h-full object-contain object-center p-2" />
                    ) : (
                      <div className="w-full h-full flex flex-col items-center justify-center gap-2 text-amethyst-300">
                        <ShoppingBag className="w-10 h-10" strokeWidth={1.2} />
                        <span className="text-[10px] uppercase tracking-widest font-medium">{p.category}</span>
                      </div>
                    )}
                  </Link>
                  <div className="p-3 sm:p-4 flex flex-col flex-1">
                    <p className="text-[10px] uppercase tracking-wider text-magenta-600 font-semibold mb-1 line-clamp-1">{p.category}</p>
                    <h3 className="font-semibold text-sm sm:text-base text-amethyst-900 line-clamp-2 leading-tight">{p.name}</h3>
                    <p className="text-[11px] text-gray-500 mt-1">by {p.seller_name}</p>
                    <div className="mt-auto pt-3">
                      <div className="flex items-baseline gap-2 mb-2">
                        <span className="text-lg font-sans tabular-nums font-semibold text-amethyst-900">₹{p.price}</span>
                        {p.mrp && p.mrp > p.price && (
                          <>
                            <span className="text-xs text-gray-400 line-through">₹{p.mrp}</span>
                            <span className="text-[10px] font-semibold text-magenta-600">{Math.round(((p.mrp - p.price) / p.mrp) * 100)}% OFF</span>
                          </>
                        )}
                      </div>
                      <WhatsAppOrderButton product={p} className="py-2 text-xs" />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 sm:hidden">
              <Link to="/marketplace" className="block" data-testid="view-all-products-mobile-btn">
                <Button variant="outline" className="w-full rounded-full border-amethyst-300 text-amethyst-700 hover:bg-amethyst-50 py-5">
                  View All Products
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Testimonials */}
      <section id="testimonials" className="py-20 lg:py-24 bg-gradient-to-b from-white via-magenta-50/30 to-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12 lg:mb-16">
            <div className="label-pill mb-4 inline-flex bg-magenta-50 text-magenta-700">Real Stories</div>
            <h2 className="text-4xl lg:text-5xl font-serif font-medium text-amethyst-900 tracking-tight">
              Women who <span className="italic gradient-text-magenta">rose</span> with us
            </h2>
            <p className="mt-4 text-gray-600">From homemakers to ₹1L/month earners — every story is real, every name is real.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((t, idx) => (
              <div key={t.name || t.quote} className={`card-premium fade-up delay-${Math.min(idx + 1, 6)} p-7 lg:p-8 flex flex-col`}>
                <div className="flex items-center gap-1 mb-4">
                  {[1,2,3,4,5].map(s => <Star key={s} className="w-4 h-4 fill-gold-400 text-gold-400" />)}
                </div>
                <p className="text-lg lg:text-xl font-serif text-amethyst-900 italic leading-relaxed mb-6 flex-1">"{t.quote}"</p>
                <div className="flex items-center gap-3 pt-4 border-t border-amethyst-100">
                  {t.img ? (
                    <img src={t.img} alt={t.name} className="w-12 h-12 rounded-full object-cover ring-2 ring-magenta-300" />
                  ) : (
                    <div className="w-12 h-12 rounded-full ring-2 ring-magenta-300 bg-gradient-to-br from-magenta-400 to-amethyst-500 flex items-center justify-center text-white font-serif font-semibold">{(t.name || "S")[0]}</div>
                  )}
                  <div>
                    <p className="font-semibold text-amethyst-900">{t.name}</p>
                    <p className="text-sm text-amethyst-600">{t.city}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Find a Leader CTA */}
      <section className="py-16 lg:py-20 bg-gradient-to-b from-white via-amethyst-50/40 to-white" data-testid="find-a-leader-section">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="relative rounded-3xl overflow-hidden p-8 sm:p-12 lg:p-16 grain"
               style={{ background: "linear-gradient(135deg, #0B1F3A 0%, #071527 60%, #050F1E 100%)" }}>
            <div className="absolute inset-0 opacity-25 pointer-events-none"
                 style={{ background: "radial-gradient(circle at 15% 20%, #E87A9C, transparent 45%), radial-gradient(circle at 85% 80%, #E87A9C, transparent 45%)" }} />
            <div className="relative grid lg:grid-cols-[1fr_auto] items-center gap-6">
              <div className="text-left max-w-2xl">
                <div className="inline-flex items-center gap-2 rounded-full border border-gold-300/40 bg-white/5 px-3 py-1 text-[10px] uppercase tracking-widest text-gold-100 font-semibold mb-3">
                  <Sparkles className="w-3 h-3 text-gold-300" /> Chapter Directory
                </div>
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-medium text-white tracking-tight">
                  Find a <span className="italic text-gold-300">Shakti Wings</span> Leader Near You
                </h2>
                <p className="mt-4 text-base text-white/85 max-w-xl">
                  Connect with a verified leader, join a local chapter, and begin your Shakti Wings journey.
                </p>
              </div>
              <Link
                to="/leaders"
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-gold-300 via-gold-400 to-gold-500 hover:from-gold-200 hover:to-gold-400 text-amethyst-900 font-bold px-7 py-4 shadow-[0_18px_50px_-15px_rgba(232, 122, 156,0.55)] transition-all whitespace-nowrap"
                data-testid="find-leader-cta"
              >
                <UsersRound className="w-5 h-5" /> Find a Leader
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-24">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="relative rounded-3xl overflow-hidden p-10 sm:p-14 lg:p-20 text-center grain" style={{background: "linear-gradient(135deg, #071527 0%, #0B1F3A 70%, #071527 100%)"}}>
            <div className="absolute inset-0 opacity-30 pointer-events-none" style={{background: "radial-gradient(circle at 30% 20%, #E87A9C, transparent 50%), radial-gradient(circle at 70% 80%, #E87A9C, transparent 50%)"}} />
            <div className="relative">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/15 backdrop-blur text-white text-xs uppercase tracking-[0.25em] font-semibold mb-6">
                <Sparkles className="w-3 h-3" strokeWidth={2} /> {t("cta.badge")}
              </div>
              <h2 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-medium text-white tracking-tight leading-tight">
                {ctaHeading}<br />
                <span className="italic gradient-text-gold">{t("cta.title.2")}</span>
              </h2>
              <p className="mt-6 text-amethyst-50 text-lg max-w-2xl mx-auto font-light">
                {ctaSubheading}
              </p>
              <Link to="/auth?mode=signup" className="inline-block mt-10" data-testid="cta-bottom-signup">
                <Button className="rounded-full px-10 py-7 text-base font-medium bg-white text-magenta-700 hover:bg-gold-50 hover:text-magenta-800 shadow-2xl">
                  {t("cta.button")}
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
              <p className="mt-5 text-xs text-amethyst-200 uppercase tracking-[0.2em]">{t("cta.trust")}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-amethyst-900 text-amethyst-200 py-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-gold-400" strokeWidth={1.5} />
            <span className="text-xl font-serif font-semibold text-white">Shakti Wings</span>
          </div>
          <PlayStoreButton testId="footer-playstore-btn" />
          <div className="text-center md:text-right space-y-1.5">
            <p className="text-sm text-amethyst-300">© 2026 Shakti Wings. Empowering women across India.</p>
            <p className="text-xs text-amethyst-300/80 flex flex-wrap gap-x-3 gap-y-1 justify-center md:justify-end" data-testid="footer-legal-links">
              <Link to="/privacy" className="hover:text-white underline">Privacy Policy</Link>
              <Link to="/terms" className="hover:text-white underline">Terms of Service</Link>
              <Link to="/account-deletion" className="hover:text-white underline">Delete My Account</Link>
              <Link to="/leaders" className="hover:text-white underline">Chapter Directory</Link>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
